import {Controller} from "stimulus";
import * as Turbo from "@hotwired/turbo";

export default class TabsController extends Controller<HTMLElement> {
  static targets = ["link", "section"];
  static values = {
    initialTab: {type: String},
    turbo: {type: Boolean, default: true},
    usingTurboFrame: {type: Boolean, default: false},
  };

  declare readonly linkTargets: HTMLAnchorElement[];
  declare readonly sectionTargets: HTMLElement[];
  declare initialTabValue: string;
  declare turboValue: boolean;
  declare usingTurboFrameValue: boolean;

  connect() {
    this.initializeTabs();
    document.addEventListener("ajax:success", this.initializeTabs);
    document.addEventListener("turbo:load", this.initializeTabs);
    document.addEventListener("turbo:frame-load", this.initializeLinks);
    document.addEventListener("tlw-tabs:navigate", this.handleNavigateEvent);
    window.addEventListener("popstate", this.initializeTabs);
    window.addEventListener("hashchange", this.initializeTabs);
  }

  disconnect() {
    document.removeEventListener("ajax:success", this.initializeTabs);
    document.removeEventListener("turbo:load", this.initializeTabs);
    document.removeEventListener("turbo:frame-load", this.initializeLinks);
    document.removeEventListener("tlw-tabs:navigate", this.handleNavigateEvent);
    window.removeEventListener("popstate", this.initializeTabs);
    window.removeEventListener("hashchange", this.initializeTabs);
  }

  /**
   * Handles the custom `tlw-tabs:navigate` event, which allows external code to
   * programmatically navigate to a specific tab.
   *
   * The event is expected to have a `detail.tab` property containing the href
   * (e.g. `#tab-name`) of the tab to navigate to. If no tab is provided, the
   * handler exits early without making any changes.
   *
   * @param event - The custom `tlw-tabs:navigate` event containing the target tab in `detail.tab`.
   */
  handleNavigateEvent = (event: CustomEvent) => {
    const tab = event.detail?.tab;
    if (!tab) return;

    this.showSection(tab);
    this.setActiveLink(tab);
    history.pushState(null, "", tab);
  };

  initializeTabs = () => {
    this.initializeTabSections();
    this.initializeLinks();
  };

  initializeTabSections = () => {
    this.showSection(window.location.hash || this.initialTabValue);
  };

  initializeLinks = () => {
    if (this.usingTurboFrameValue) {
      this.setActiveLink((this.element.closest("turbo-frame") as Turbo.FrameElement).src || "");
    } else {
      this.setActiveLink(window.location.hash || this.initialTabValue);
    }
  };

  visit(event) {
    if (event.params.validate) {
      const activeTab = this.sectionTargets.find(section => section.dataset.active && section.contains(event.target));
      const formFields = activeTab?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, select, textarea");

      if (formFields?.length && formFields.length > 0) {
        const allFieldsAreValid = [...formFields].every(field => {
          const isValid = field.checkValidity();
          if (!isValid) field.reportValidity();
          return isValid;
        });

        if (!allFieldsAreValid) event.preventDefault();
      }
    }

    if (event.defaultPrevented) return;

    const href = event.currentTarget.getAttribute("href") || event.currentTarget.getAttribute("data-tab");

    if (href && href[0] == "#") {
      event.preventDefault(); // Prevent scrolling to element

      this.setActiveLink(href);
      this.showSection(href);

      if (!this.turboValue) {
        history.pushState(null, "", href); // Emulate browser behaviour
        return;
      }

      Turbo.cache.clear(); // Prevent flash of previously rendered tab on change

      // Update browser history if below visit not cancelled
      addEventListener("turbo:visit", function handleVisitEvent({detail: {action, url}}: Turbo.TurboVisitEvent) {
        if (action === "replace" && url.indexOf(href) > -1) {
          history.pushState(null, "", href);
        }
        removeEventListener("turbo:visit", handleVisitEvent);
      });

      // We need to use "replace" to trigger visit event.
      // Advance doesn't work with locaiton.hash
      Turbo.visit(
        window.location.pathname + window.location.search + href,
        {action: "replace"}
      );
    }
  }

  showSection(href: string) {
    const section =
      this.sectionTargets.find((el: HTMLElement) => el.id === href.slice(1)) ||
      this.sectionTargets[0];

    if (!section) return;

    // Hide tab sections
    this.sectionTargets.forEach(element => {
      element.style.display = "none";
      element.removeAttribute("data-active");
    });

    // Show active section
    section.style.display = "block";
    section.setAttribute("data-active", "true");
  }

  setActiveLink(href: string) {
    const link =
      this.linkTargets.find((el: HTMLAnchorElement) => el.href.indexOf(href) > -1) ||
      this.linkTargets[0];

    if (!link) return;

    // Make all links inactive
    this.linkTargets.forEach(element => element.removeAttribute("data-active"));

    // Activate selected link
    link.setAttribute("data-active", "true");
    link.scrollIntoView({block: "nearest", inline: "nearest"});
  }
}
